import { Theme, useTheme } from "@/components/theme/themeProvider"
import { Button } from "@/components/ui/baseUi/button"
import { cn } from "@/modules/lib/utils"
import data from "@emoji-mart/data"
import EmojiPicker from "@emoji-mart/react"
import { Heart } from "lucide-react"
import { useRef, useState } from "react"

const CURATED_REACTIONS = ["❤️", "💪", "👊", "🥵", "😱"]

type SocialReactionPickerProps = {
  currentEmoji: string | null
  onSelect: (emoji: string) => void
  onRemove: () => void
  disabled?: boolean
}

export function SocialReactionPicker({ currentEmoji, onSelect, onRemove, disabled }: SocialReactionPickerProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [showFullPicker, setShowFullPicker] = useState(false)
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const { theme } = useTheme()

  const handleMouseEnter = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current)
    if (!disabled) setIsOpen(true)
  }

  const handleMouseLeave = () => {
    closeTimer.current = setTimeout(() => {
      setIsOpen(false)
      setShowFullPicker(false)
    }, 200)
  }

  const handleEmojiClick = (emoji: string) => {
    if (emoji === currentEmoji) {
      onRemove()
    } else {
      onSelect(emoji)
    }
    setIsOpen(false)
    setShowFullPicker(false)
  }

  const handleFullPickerSelect = (emoji: { native: string }) => {
    if (emoji.native === currentEmoji) {
      onRemove()
    } else {
      onSelect(emoji.native)
    }
    setIsOpen(false)
    setShowFullPicker(false)
  }

  return (
    <div ref={containerRef} className="relative" onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
      {/* Compact reaction bar */}
      {isOpen && !showFullPicker && (
        <div
          className="absolute right-0 bottom-12 z-50 mb-1 flex items-center gap-1 rounded-full border border-interactive-border-neutral-faded bg-surface-background-default px-3 py-2 shadow-lg"
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          {CURATED_REACTIONS.map((emoji) => (
            <button
              key={emoji}
              onClick={() => handleEmojiClick(emoji)}
              className={cn(
                "flex size-8 items-center justify-center rounded-full text-lg leading-none transition-all duration-150 hover:scale-150",
                emoji === currentEmoji && "bg-surface-background-float",
              )}
            >
              {emoji}
            </button>
          ))}
          <button
            onClick={() => setShowFullPicker(true)}
            className="flex size-8 items-center justify-center rounded-full bg-surface-background-float text-sm font-semibold text-textOnSurface-default transition-colors hover:bg-interactive-background-neutral-highlighted"
          >
            +
          </button>
        </div>
      )}

      {/* Full emoji-mart picker */}
      {showFullPicker && (
        <div
          className="absolute -top-12 right-0 z-50 mb-1"
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          <EmojiPicker
            data={data}
            onEmojiSelect={handleFullPickerSelect}
            theme={theme === Theme.OwnerDark ? "dark" : "light"}
            previewPosition="none"
            skinTonePosition="none"
          />
        </div>
      )}

      {/* Trigger button */}
      <Button
        variant="secondarySecondary"
        size="iconLg"
        disabled={disabled}
        onClick={() => {
          if (currentEmoji) {
            handleEmojiClick(currentEmoji)
          } else {
            handleEmojiClick("❤️")
          }
        }}
      >
        {currentEmoji ? (
          <span className="text-2xl leading-none">{currentEmoji}</span>
        ) : (
          <Heart className="size-6 text-textOnSurface-default" />
        )}
      </Button>
    </div>
  )
}
